<script setup lang="ts">
import polyline from '@mapbox/polyline'
import createGpx from 'gps-to-gpx'
import emojiTree from 'emoji-tree'
import FileSaver from 'file-saver'

import { ANALYTICS_EVENTS } from '~/types/analyticsEvents'

import type { MainSectionPhoto, TripData } from '~/types/trip'
import type { BikeData } from '~/types/bikes'
import type { PostingComment } from '~/types/postings'
import type { Map as MapLibreMap } from 'maplibre-gl'
import type { MapStyleKey } from '~/composables/useMapStyles'

const trip = defineModel<TripData>('trip', { required: true })

const props = withDefaults(
  defineProps<{
    comments: PostingComment[]
    sectionPolyline?: string
    bike?: BikeData | null
  }>(),
  {
    sectionPolyline: undefined,
    bike: null
  }
)

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const mainStore = useMainStore()
const { getSectionLocations } = useSectionService()
const { likeTrip, unlikeTrip } = useLikeService()
const { requirePro } = useProGate()
const { trackEvent } = useAnalyticsTrackingService()

const state = reactive({
  maximized: false,
  mapStyleKey: 'street' as MapStyleKey,
  likeLoading: false,
  map: undefined as MapLibreMap | undefined
})

const { isLoggedIn, requireLogin } = useAuthGate()

const isUserPro = computed(() => authStore.isUserPro)

const isOwner = computed(() => {
  if (authStore.currentUser) {
    return authStore.currentUser.id === trip.value.user_id
  }

  return false
})

const onRideThisTrip = (event: MouseEvent) => {
  if (!requireLogin(undefined, { returnPath: `/routeplanner?trip_id=${trip.value.id}` })) {
    event.preventDefault()
  }
}

const polylineCoordinates = computed<Array<[number, number]>>(() => {
  if (!props.sectionPolyline) {
    return []
  }

  return polyline
    .decode(props.sectionPolyline, 6)
    .map((coordinate) => [coordinate[1], coordinate[0]])
})

const showMaxSpeed = computed(() => !trip.value.hide_max_speed)

const participantIds = computed(() => [trip.value.user_id, ...trip.value.friend_ids])

const sortedPhotos = computed<MainSectionPhoto[]>(() => {
  if (!trip.value.main_section.photos) return []

  return [...trip.value.main_section.photos].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  )
})

const nonAiPhotos = computed(() => sortedPhotos.value.filter((photo) => !photo.ai))

const previewPhotos = computed(() => sortedPhotos.value.slice(0, 6))

const extraPhotosCount = computed(() =>
  Math.max(0, sortedPhotos.value.length - previewPhotos.value.length)
)

const showAiImageCard = computed(() => isOwner.value && nonAiPhotos.value.length > 0)

const hasBodyContent = computed(() => !!trip.value.note || previewPhotos.value.length > 0)

const privacyLabel = computed(() => {
  const labels: Record<string, string> = {
    anyone: t('Anyone'),
    friends: t('Followers'),
    me: t('Only me')
  }

  return labels[trip.value.privacy] ?? trip.value.privacy
})

const bikeLabel = computed(() => {
  if (!props.bike) return ''

  return props.bike.nickname || `${props.bike.brand.name} ${props.bike.model.name}`
})

const showLikeList = () => {
  mainStore.showLikeListModal('trips', trip.value.id)
}

const openPhoto = (photoId: number) => {
  router.push({
    path: `/trips/${trip.value.id}/photos`,
    query: { pid: photoId }
  })
}

const openFirstEligibleAiPhoto = () => {
  const photo = nonAiPhotos.value[0]

  if (photo == null) return

  openPhoto(photo.id)
}

const updateTrip = (data: TripData) => {
  trip.value = { ...trip.value, ...data }
}

const fullscreen = () => {
  state.maximized = !state.maximized

  const map = state.map

  setTimeout(() => map?.resize(), 0)
}

const toggleLike = async () => {
  state.likeLoading = true

  try {
    if (trip.value.liked_by_me) {
      await unlikeTrip(trip.value.id)

      trip.value.liked_by_me = false
      trip.value.likes_count--
    } else {
      await likeTrip(trip.value.id)

      trip.value.liked_by_me = true
      trip.value.likes_count++
    }
  } catch (error) {
    console.error('Error toggling like', error)
  } finally {
    state.likeLoading = false
  }
}

const exportGPX = () => {
  requirePro('gpx_export_trip', async () => {
    try {
      const tree = emojiTree(trip.value.title)
      const title = tree
        .filter(({ type }: { type: string }) => type !== 'emoji')
        .map(({ text }: { type: string; text: string }) => text)
        .join('')

      const locations = await getSectionLocations(trip.value.main_section.id)

      if (!locations) return

      const waypoints = locations.map((point) => ({
        ...point,
        elevation: point.altitude,
        time: point.timestamp
      }))

      const gpx = createGpx(waypoints, {
        activityName: title,
        startTime: trip.value.date,
        creator: 'riserapp.com'
      })
      const blob = new Blob([gpx], { type: 'text/plain;charset=utf-8' })

      FileSaver.saveAs(blob, 'cardo-ride-trip.gpx')
    } catch (err) {
      console.error(err)
      throw err
    }
  })
}

onMounted(() => {
  trackEvent(ANALYTICS_EVENTS.tripDetailShow())
})

onBeforeUnmount(() => {
  if (state.maximized) {
    mainStore.unhideScrollbar()
  }
})
</script>

<template>
  <TripHead :trip="trip" />
  <section>
    <div class="card tw-overflow-hidden" :class="{ 'map-card-maximized': state.maximized }">
      <TripMap
        class="tw-relative tw-z-[2] tw-h-full tw-w-full tw-flex-grow"
        :section-polyline="sectionPolyline"
        :section-photos="trip.main_section.photos"
        :map-style-key="state.mapStyleKey"
        height-class="tw-h-[450px]"
        @open-image-id="openPhoto"
        @map="state.map = $event"
      >
        <button
          class="normalize-style tw-absolute tw-right-2 tw-top-2 tw-z-10 tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-bg-white"
          @click="fullscreen"
        >
          <FontAwesomeIcon v-if="!state.maximized" fixed-width icon="expand" />
          <FontAwesomeIcon v-else fixed-width icon="compress" />
        </button>

        <div class="tw-absolute tw-left-2 tw-top-2 tw-z-10">
          <MapStylePicker v-model="state.mapStyleKey" direction="bottom-right" />
        </div>

        <ClientOnly>
          <NuxtLink
            v-if="isLoggedIn && participantIds.length"
            :to="`/trips/${trip.id}/participants`"
            class="tw-absolute tw-bottom-2 tw-left-2 tw-z-10"
          >
            <MultiUserImageBadge
              :user-ids="participantIds"
              :max-visible-users="participantIds.length === 4 ? 4 : 3"
              user-image-classes="tw-w-10 tw-h-10 tw-border tw-border-white -tw-ml-5"
            />
          </NuxtLink>
        </ClientOnly>
      </TripMap>

      <!-- Trip statistics -->
      <section
        class="tw-grid tw-grid-cols-3 tw-gap-2"
        :class="[showMaxSpeed ? '2xl:tw-grid-cols-6' : '2xl:tw-grid-cols-5']"
      >
        <div class="stat-block">
          <span>
            <UnitItem :value="trip.main_section.distance">
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </UnitItem>
          </span>
          <br />
          <span class="tw-text-black/50">{{ t('Distance') }}</span>
        </div>

        <div class="stat-block">
          <span
            ><strong><DurationItem :duration="trip.main_section.duration" /></strong
          ></span>
          <br />
          <span class="tw-text-black/50">{{ t('Duration') }}</span>
        </div>

        <div v-if="showMaxSpeed" class="stat-block">
          <span>
            <UnitItem
              current-unit="m/s"
              unit-to-metric="km/h"
              unit-to-imperical="mph"
              :value="trip.max_speed"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </UnitItem>
          </span>
          <br />
          <span class="tw-text-black/50">{{ t('Max Speed') }}</span>
        </div>

        <div class="stat-block">
          <span>
            <UnitItem
              current-unit="m/s"
              unit-to-metric="km/h"
              unit-to-imperical="mph"
              :value="trip.avg_speed"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </UnitItem>
          </span>
          <br />
          <span class="tw-text-black/50">{{ t('avg speed') }}</span>
        </div>

        <div class="stat-block">
          <span>
            <UnitItem
              unit-to-metric="m"
              unit-to-imperical="ft"
              :value="trip.main_section.elevation_climb"
              :number-format="{ maximumFractionDigits: 0, minimumFractionDigits: 0 }"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </UnitItem>
          </span>
          /
          <span>
            <UnitItem
              unit-to-metric="m"
              unit-to-imperical="ft"
              :value="-trip.main_section.elevation_fall"
              :number-format="{ maximumFractionDigits: 0, minimumFractionDigits: 0 }"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </UnitItem>
          </span>
          <br />
          <span class="tw-text-black/50">{{ t('Climb / Fall') }}</span>
        </div>

        <div class="stat-block">
          <span>
            <UnitItem
              unit-to-metric="m"
              unit-to-imperical="ft"
              :value="trip.main_section.max_elevation"
              :number-format="{ maximumFractionDigits: 0, minimumFractionDigits: 0 }"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </UnitItem>
          </span>
          /
          <span>
            <UnitItem
              unit-to-metric="m"
              unit-to-imperical="ft"
              :value="trip.main_section.min_elevation"
              :number-format="{ maximumFractionDigits: 0, minimumFractionDigits: 0 }"
            >
              <template #value="{ value }"
                ><strong>{{ value }}</strong></template
              >
              <template #unit="{ unit }"> {{ unit }}</template>
            </UnitItem>
          </span>
          <br />
          <span class="tw-text-black/50">{{ t('Sea level (max/min)') }}</span>
        </div>
      </section>

      <NuxtPage :trip="trip" @update="updateTrip" />
    </div>

    <div class="tw-flex tw-flex-wrap tw-gap-4 tw-pt-5 lg:tw-flex-nowrap">
      <div class="tw-flex tw-flex-grow tw-flex-col tw-gap-4 tw-overflow-hidden">
        <ClientOnly>
          <div
            v-if="isOwner && (polylineCoordinates.length > 0 || showAiImageCard)"
            class="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row"
          >
            <TripRewindCard
              v-if="polylineCoordinates.length > 0"
              class="tw-flex-1"
              :trip="trip"
              :polyline-coordinates="polylineCoordinates"
              @created="trip.rewind_render_status = 'PENDING'"
            />

            <TripAiImageCard
              v-if="showAiImageCard"
              class="tw-flex-1"
              @click="openFirstEligibleAiPhoto"
            />
          </div>
        </ClientOnly>

        <ClientOnly>
          <section
            v-if="trip.duplicate_of != null"
            class="card tw-relative tw-flex tw-items-center tw-gap-4 tw-px-6 tw-py-4"
          >
            <div class="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-riser-orange/10" />

            <FontAwesomeIcon icon="triangle-exclamation" class="tw-text-2xl tw-text-riser-orange" />

            <p>
              <NuxtLink v-if="isOwner" :to="`/trips/${trip.duplicate_of}`">
                {{ t('challenge-trip-duplicate-owner-info-message') }}
              </NuxtLink>

              <template v-else>
                {{ t('challenge-trip-duplicate-others-info-message') }}
              </template>
            </p>
          </section>
        </ClientOnly>

        <section
          v-if="trip.implausible"
          class="card tw-relative tw-flex tw-items-center tw-gap-4 tw-px-6 tw-py-4"
        >
          <div class="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-riser-orange/10" />

          <FontAwesomeIcon icon="triangle-exclamation" class="tw-text-2xl tw-text-riser-orange" />

          <p>{{ t('challenge-trip-implausible-info-message') }}</p>
        </section>

        <section class="card tw-px-6 tw-py-4">
          <div :class="{ 'tw-mb-4 tw-border-b tw-border-black/10 tw-pb-4': hasBodyContent }">
            <div class="tw-flex tw-items-center tw-justify-between">
              <h1 class="tw-truncate tw-text-lg tw-font-bold">{{ trip.title || t('Trip') }}</h1>

              <span>
                <ClientOnly>
                  {{ formatDate(new Date(trip.date)) }}

                  <template #fallback> - </template>
                </ClientOnly>
              </span>
            </div>

            <ClientOnly>
              <div v-if="bike || isOwner" class="tw-mt-2 tw-flex tw-items-center tw-gap-4">
                <NuxtLink
                  v-if="bike"
                  :to="`/bikes/${bike.id}`"
                  class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-black/60 hover:tw-text-black/80"
                >
                  <img
                    :src="bike.photo_thumb_url || '/images/placeholder/bikeImagePlaceholderLight.png'"
                    class="tw-h-6 tw-w-6 tw-rounded-full tw-object-cover"
                  />
                  <span>{{ bikeLabel }}</span>
                </NuxtLink>

                <p v-if="isOwner" class="tw-ml-auto tw-text-sm tw-text-black/50">
                  {{ t('Privacy') }}:
                  <NuxtLink :to="`/trips/${trip.id}/edit`" class="tw-text-riser-blue">
                    {{ privacyLabel }}
                  </NuxtLink>
                </p>
              </div>
            </ClientOnly>
          </div>

          <p v-if="trip.note" class="tw-mt-2 tw-whitespace-pre-wrap">
            {{ trip.note }}
          </p>

          <div
            v-if="previewPhotos.length"
            class="tw-mt-4 tw-grid tw-grid-cols-3 tw-gap-1 sm:tw-grid-cols-6"
          >
            <NuxtLink
              v-for="(photo, index) in previewPhotos"
              :key="photo.id"
              :to="`/trips/${trip.id}/photos?pid=${photo.id}`"
              class="tw-relative tw-block tw-overflow-hidden tw-rounded"
            >
              <img
                loading="lazy"
                class="tw-aspect-square tw-w-full tw-cursor-pointer tw-object-cover"
                :src="photo.thumb"
              />

              <div
                v-if="extraPhotosCount > 0 && index === previewPhotos.length - 1"
                class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-text-xl tw-font-bold tw-text-white"
              >
                +{{ extraPhotosCount }}
              </div>
            </NuxtLink>
          </div>
        </section>

        <section class="card tw-p-6">
          <div
            class="tw-mb-4 tw-flex tw-items-center tw-gap-6 tw-border-b tw-border-black/10 tw-pb-4 tw-text-sm"
          >
            <button
              class="normalize-style tw-flex tw-cursor-pointer tw-items-center tw-gap-2"
              @click="showLikeList"
            >
              <IconsFlame class="tw-h-4 tw-w-auto tw-text-riser-red" />
              <span
                ><strong>{{ trip.likes_count }}</strong> {{ t('Likes') }}</span
              >
            </button>

            <div class="tw-flex tw-items-center tw-gap-2 tw-text-black/60">
              <FontAwesomeIcon icon="comment" class="tw-text-black/40" />
              <span
                ><strong>{{ trip.comments_count }}</strong> {{ t('Comments') }}</span
              >
            </div>

            <AppLikeButton
              :is-liked="trip.liked_by_me"
              :loading="state.likeLoading"
              :classes="[
                'tw-ml-auto tw-rounded-[10px] tw-px-2.5 tw-py-[5px] tw-text-sm tw-shadow tw-transition-all',
                trip.liked_by_me
                  ? 'tw-bg-white tw-text-riser-red tw-ring-1 tw-ring-inset tw-ring-riser-red'
                  : 'tw-bg-gradient-to-r tw-from-riser-red tw-to-riser-orange tw-text-white'
              ]"
              @click="toggleLike"
            >
              <IconsFlame class="tw-h-4 tw-w-auto" />

              <span>{{ trip.liked_by_me ? t('Liked') : t('Like') }}</span>
            </AppLikeButton>
          </div>

          <ClientOnly>
            <Comments
              type="trip"
              class="comments"
              :comments="comments"
              :endpoint="`trips/${trip.id}/comments`"
              :visible="10"
            />
          </ClientOnly>
        </section>
      </div>

      <div class="tw-flex tw-w-full tw-flex-shrink-0 tw-flex-col tw-gap-4 lg:tw-w-72 xl:tw-w-80">
        <div class="tw-flex tw-flex-col tw-gap-4">
          <AppLikeButton
            :is-liked="trip.liked_by_me"
            :loading="state.likeLoading"
            :classes="[
              'tw-w-full tw-justify-center tw-rounded-[10px] tw-p-2.5 tw-text-center tw-shadow tw-transition-all',
              trip.liked_by_me
                ? 'tw-bg-white tw-text-riser-red tw-ring-1 tw-ring-inset tw-ring-riser-red'
                : 'tw-bg-gradient-to-r tw-from-riser-red tw-to-riser-orange tw-text-white'
            ]"
            @click="toggleLike"
          >
            <IconsFlame class="tw-h-5 tw-w-auto" />

            <span>{{ trip.liked_by_me ? t('Liked') : t('Like') }}</span>
          </AppLikeButton>

          <NuxtLink
            class="button blue-button tw-mb-4 tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-text-center tw-shadow"
            :to="`/trips/${trip.id}/share`"
          >
            <FontAwesomeIcon icon="share" />

            <span>{{ t('Share') }}</span>
          </NuxtLink>

          <ClientOnly>
            <NuxtLink
              v-if="isOwner"
              class="button tw-w-full tw-border-gray-200 tw-bg-gray-200 tw-text-center tw-text-black tw-shadow"
              :to="`/trips/${trip.id}/edit`"
            >
              {{ t('Edit Trip') }}
            </NuxtLink>
          </ClientOnly>

          <NuxtLink
            class="button tw-w-full tw-border-gray-200 tw-bg-gray-200 tw-text-center tw-text-black tw-shadow"
            :to="`/routeplanner?trip_id=${trip.id}`"
            @click="onRideThisTrip"
          >
            {{ t('ride-this-trip') }}
          </NuxtLink>

          <div v-if="isOwner" class="tw-relative">
            <button
              class="button tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-border-gray-200 tw-bg-gray-200 tw-text-center tw-text-black tw-shadow"
              @click="exportGPX"
            >
              <span>{{ t('Export-as-gpx') }}</span>

              <ClientOnly>
                <IconsProBadge v-if="!isUserPro" class="tw-w-4" />
              </ClientOnly>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.stat-block {
  @apply tw-flex-1 tw-px-1 tw-py-2;
}

.map-card-maximized {
  @apply tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-top-top-bar tw-z-[100] tw-flex tw-h-auto tw-w-auto tw-flex-col;
}
</style>
